import React, { useState, useEffect } from 'react';
import { Input, Card, Typography, Timeline, Tag, Spin, message, Row, Col, Alert, Button, Dropdown } from 'antd';
import { SearchOutlined, RocketOutlined, CheckCircleOutlined, ClockCircleOutlined, EnvironmentOutlined, DownloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import './App.css';

const { Search } = Input;
const { Title, Text } = Typography;

// API基础URL配置
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
// Admin API URL（用于获取客户信息）
const ADMIN_API_URL = process.env.REACT_APP_ADMIN_API_URL || 'http://localhost:5001';

function App() {
  const [loading, setLoading] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [language, setLanguage] = useState(() => {
    // 从localStorage读取语言设置,默认为中文
    return localStorage.getItem('language') || 'zh';
  });
  const [searchValue, setSearchValue] = useState('');

  // 客户信息（根据URL后缀动态加载）
  const [customerInfo, setCustomerInfo] = useState(null);
  const [customerSlug, setCustomerSlug] = useState('');

  // 网站配置（默认值，会被客户信息覆盖）
  // 有后缀时：display_name有值则显示，无值则不显示文字
  // 无后缀（主域名）时：显示默认平台名称
  const siteConfig = {
    companyName: {
      zh: customerSlug ? (customerInfo?.display_name || '') : '通用订单查询平台',
      en: customerSlug ? (customerInfo?.display_name || '') : 'Universal Order Tracking'
    },
    logoIcon: 'rocket', // 可配置: rocket, truck, plane等
    logoUrl: customerInfo?.logo || null // 客户自定义logo
  };

  // 语言文本配置
  const texts = {
    zh: {
      title: '通用订单查询平台',
      searchPlaceholder: '请输入快递单号',
      searchButton: '查询',
      searching: '正在查询中...',
      trackingNumber: '快递单号',
      currentStatus: '当前状态',
      sender: '发件地',
      receiver: '收件地',
      orderTime: '下单时间',
      totalPieces: '总件数',
      latestStatus: '最新状态',
      timeline: '物流轨迹',
      download: '下载物流信息',
      downloadTxt: '下载为TXT',
      downloadExcel: '下载为Excel',
      downloadWord: '下载为Word',
      footer: '© 2024 通用订单查询平台 - 支持多家物流公司实时查询',
      orderNo: '入仓单号',
      statusLabel: '货况',
      timeLabel: '时间',
      searchWarning: '请输入快递单号',
      searchError: '查询失败，请检查单号或稍后重试',
      downloadSuccess: '文件下载成功',
      downloadError: '文件下载失败',
      delivered: '已签收',
      inTransit: '运输中',
      outForDelivery: '派送中'
    },
    en: {
      title: 'Universal Order Tracking',
      searchPlaceholder: 'Enter tracking number',
      searchButton: 'Search',
      searching: 'Searching...',
      trackingNumber: 'Tracking Number',
      currentStatus: 'Current Status',
      sender: 'Sender',
      receiver: 'Receiver',
      orderTime: 'Order Time',
      totalPieces: 'Total Pieces',
      latestStatus: 'Latest Status',
      timeline: 'Timeline',
      download: 'Download',
      downloadTxt: 'Download as TXT',
      downloadExcel: 'Download as Excel',
      downloadWord: 'Download as Word',
      footer: '© 2024 Universal Order Tracking - Support Multiple Logistics Companies',
      orderNo: 'Order No',
      statusLabel: 'Status',
      timeLabel: 'Time',
      searchWarning: 'Please enter tracking number',
      searchError: 'Query failed, please check the number or try again later',
      downloadSuccess: 'File downloaded successfully',
      downloadError: 'File download failed',
      delivered: 'Delivered',
      inTransit: 'In Transit',
      outForDelivery: 'Out for Delivery'
    }
  };

  const t = texts[language]; // 当前语言文本

  // 切换语言
  const toggleLanguage = () => {
    const newLang = language === 'zh' ? 'en' : 'zh';
    setLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  // 标记slug是否已初始化
  const [slugInitialized, setSlugInitialized] = useState(false);

  // 从URL路径获取客户slug并加载客户信息
  useEffect(() => {
    const loadCustomerInfo = async () => {
      // 从URL路径获取slug，例如 /abc/ 或 /luo/
      const pathParts = window.location.pathname.split('/').filter(p => p);
      const slug = pathParts[0] || '';

      if (slug) {
        setCustomerSlug(slug);
        try {
          // 调用Admin API获取客户信息
          const response = await axios.get(`${ADMIN_API_URL}/api/customer/${slug}`);
          if (response.data.success) {
            // 处理logo URL，确保是完整路径
            const data = response.data.data;
            if (data.logo && !data.logo.startsWith('http')) {
              data.logo = `${ADMIN_API_URL}${data.logo}`;
            }
            setCustomerInfo(data);
            // 设置页面标题为客户名称
            const titleName = data.display_name || data.company_name || '';
            document.title = titleName ? `物流查询 - ${titleName}` : '物流查询';
          }
        } catch (error) {
          console.log('未找到客户信息，使用默认配置');
        }
      }
      // 标记slug初始化完成
      setSlugInitialized(true);
    };

    loadCustomerInfo();
  }, []);

  // 从URL参数获取订单号并自动查询（等待slug初始化完成后再执行）
  useEffect(() => {
    // 等待slug初始化完成
    if (!slugInitialized) return;

    // 支持的URL参数格式:
    // ?tracking=单号
    // ?order=单号
    // ?no=单号
    // ?trackingNumber=单号
    const loadFromUrl = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const trackingNumber = urlParams.get('tracking') ||
                            urlParams.get('order') ||
                            urlParams.get('no') ||
                            urlParams.get('trackingNumber');

      if (trackingNumber) {
        setSearchValue(trackingNumber);
        handleSearch(trackingNumber);
      }
    };

    // 页面加载时执行
    loadFromUrl();

    // 监听浏览器前进/后退
    const handlePopState = () => {
      loadFromUrl();
    };

    window.addEventListener('popstate', handlePopState);

    // 清理事件监听
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [slugInitialized]); // 当slugInitialized变为true时执行

  const handleSearch = async (value) => {
    if (!value.trim()) {
      message.warning(t.searchWarning);
      return;
    }

    setLoading(true);
    try {
      // 直接从URL路径获取slug（避免闭包问题）
      const pathParts = window.location.pathname.split('/').filter(p => p);
      const currentSlug = pathParts[0] || '';

      let url;
      // 如果有客户slug，调用admin的API（支持单号映射和延迟设置）
      // 否则调用backend的API（直接查询物流）
      if (currentSlug) {
        url = `${ADMIN_API_URL}/api/track/${value}?slug=${encodeURIComponent(currentSlug)}`;
      } else {
        url = `${API_BASE_URL}/track/${value}`;
      }

      // 调试日志
      console.log('currentSlug:', currentSlug);
      console.log('ADMIN_API_URL:', ADMIN_API_URL);
      console.log('请求URL:', url);

      // 调用API
      const response = await axios.get(url);
      setTrackingData(response.data);

      // 更新URL参数,方便分享
      const newUrl = `${window.location.pathname}?tracking=${encodeURIComponent(value)}`;
      window.history.pushState({ tracking: value }, '', newUrl);
    } catch (error) {
      message.error(t.searchError);
      console.error('查询错误:', error);
    } finally {
      setLoading(false);
    }
  };

  // 根据状态返回对应的图标和颜色
  const getStatusConfig = (status) => {
    const configs = {
      '已签收': { icon: <CheckCircleOutlined />, color: 'success' },
      '运输中': { icon: <RocketOutlined />, color: 'processing' },
      '派送中': { icon: <ClockCircleOutlined />, color: 'warning' },
    };
    return configs[status] || { icon: <ClockCircleOutlined />, color: 'default' };
  };

  // 下载为TXT格式
  const downloadAsTxt = () => {
    if (!trackingData) return;

    let content = `物流查询信息\n`;
    content += `=`.repeat(50) + `\n\n`;
    content += `快递单号: ${trackingData.tracking_number}\n`;
    content += `当前状态: ${trackingData.status}\n`;
    if (trackingData.sender) content += `发件地: ${trackingData.sender}\n`;
    if (trackingData.receiver) content += `收件地: ${trackingData.receiver}\n`;
    if (trackingData.latest_status) content += `最新状态: ${trackingData.latest_status}\n`;
    content += `\n` + `=`.repeat(50) + `\n\n`;
    content += `物流轨迹:\n\n`;

    // EXP物流多订单格式
    if (trackingData.courier === 'EXP物流' && trackingData.orders) {
      trackingData.orders.forEach((order, index) => {
        content += `订单 ${index + 1}:\n`;
        content += `  入仓单号: ${order.order_no}\n`;
        content += `  下单时间: ${order.order_time}\n`;
        content += `  总件数: ${order.total_pieces}\n\n`;
        order.timeline.forEach((item, idx) => {
          content += `  ${idx + 1}. [${item.time}]\n`;
          content += `     ${item.description}\n`;
          if (item.description_en) content += `     ${item.description_en}\n`;
          content += `\n`;
        });
        content += `\n`;
      });
    } else {
      // 正常Timeline格式
      trackingData.timeline.forEach((item, index) => {
        content += `${index + 1}. [${item.time}]`;
        if (item.location) content += ` - ${item.location}`;
        if (item.location_en) content += ` / ${item.location_en}`;
        content += `\n   ${item.description}\n`;
        if (item.description_en) content += `   ${item.description_en}\n`;
        content += `\n`;
      });
    }

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `物流查询_${trackingData.tracking_number}.txt`;
    link.click();
    URL.revokeObjectURL(url);
    message.success(`TXT ${t.downloadSuccess}`);
  };

  // 下载为Excel格式
  const downloadAsExcel = async () => {
    if (!trackingData) return;

    try {
      // 动态导入xlsx库
      const XLSX = await import('xlsx');

      // 准备基本信息数据
      const basicInfo = [
        ['物流查询信息', ''],
        ['快递单号', trackingData.tracking_number],
        ['当前状态', trackingData.status],
        ['发件地', trackingData.sender || '-'],
        ['收件地', trackingData.receiver || '-'],
        ['最新状态', trackingData.latest_status || '-'],
        ['', ''],
      ];

      let timelineData = [];

      // EXP物流多订单格式
      if (trackingData.courier === 'EXP物流' && trackingData.orders) {
        trackingData.orders.forEach((order, index) => {
          timelineData.push([`订单 ${index + 1}`, '', '', '']);
          timelineData.push(['入仓单号', order.order_no, '', '']);
          timelineData.push(['下单时间', order.order_time, '', '']);
          timelineData.push(['总件数', order.total_pieces, '', '']);
          timelineData.push(['序号', '时间', '货况', '']);
          order.timeline.forEach((item, idx) => {
            timelineData.push([
              idx + 1,
              item.time,
              item.description,
              item.description_en || ''
            ]);
          });
          timelineData.push(['', '', '', '']);
        });
      } else {
        // 正常Timeline格式
        timelineData.push(['序号', '时间', '地点', '描述', '英文描述']);
        trackingData.timeline.forEach((item, index) => {
          timelineData.push([
            index + 1,
            item.time,
            item.location || '-',
            item.description,
            item.description_en || ''
          ]);
        });
      }

      // 合并数据
      const allData = [...basicInfo, ['物流轨迹', ''], ...timelineData];

      // 创建工作簿和工作表
      const ws = XLSX.utils.aoa_to_sheet(allData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '物流信息');

      // 设置列宽
      ws['!cols'] = [
        { wch: 15 },
        { wch: 25 },
        { wch: 30 },
        { wch: 30 }
      ];

      // 导出文件
      XLSX.writeFile(wb, `物流查询_${trackingData.tracking_number}.xlsx`);
      message.success(`Excel ${t.downloadSuccess}`);
    } catch (error) {
      console.error('Excel导出错误:', error);
      message.error(`Excel ${t.downloadError}`);
    }
  };

  // 下载为Word格式
  const downloadAsWord = async () => {
    if (!trackingData) return;

    try {
      // 动态导入docx库
      const docx = await import('docx');
      const { Document, Paragraph, TextRun, Table, TableCell, TableRow, AlignmentType, WidthType } = docx;

      // 创建文档内容
      const children = [
        new Paragraph({
          text: '物流查询信息',
          heading: docx.HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
        }),
        new Paragraph({ text: '' }),
        new Paragraph({
          children: [
            new TextRun({ text: '快递单号: ', bold: true }),
            new TextRun(trackingData.tracking_number),
          ],
        }),
        new Paragraph({
          children: [
            new TextRun({ text: '当前状态: ', bold: true }),
            new TextRun(trackingData.status),
          ],
        }),
      ];

      if (trackingData.sender) {
        children.push(new Paragraph({
          children: [
            new TextRun({ text: '发件地: ', bold: true }),
            new TextRun(trackingData.sender),
          ],
        }));
      }

      if (trackingData.receiver) {
        children.push(new Paragraph({
          children: [
            new TextRun({ text: '收件地: ', bold: true }),
            new TextRun(trackingData.receiver),
          ],
        }));
      }

      if (trackingData.latest_status) {
        children.push(new Paragraph({
          children: [
            new TextRun({ text: '最新状态: ', bold: true }),
            new TextRun(trackingData.latest_status),
          ],
        }));
      }

      children.push(
        new Paragraph({ text: '' }),
        new Paragraph({
          text: '物流轨迹',
          heading: docx.HeadingLevel.HEADING_2,
        }),
        new Paragraph({ text: '' })
      );

      // EXP物流多订单格式
      if (trackingData.courier === 'EXP物流' && trackingData.orders) {
        trackingData.orders.forEach((order, index) => {
          children.push(
            new Paragraph({
              text: `订单 ${index + 1}`,
              heading: docx.HeadingLevel.HEADING_3,
            }),
            new Paragraph({
              children: [
                new TextRun({ text: '入仓单号: ', bold: true }),
                new TextRun(order.order_no),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: '下单时间: ', bold: true }),
                new TextRun(order.order_time),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: '总件数: ', bold: true }),
                new TextRun(order.total_pieces),
              ],
            }),
            new Paragraph({ text: '' })
          );

          // 创建表格
          const tableRows = [
            new TableRow({
              children: [
                new TableCell({ children: [new Paragraph({ text: '时间', bold: true })] }),
                new TableCell({ children: [new Paragraph({ text: '货况', bold: true })] }),
              ],
            }),
          ];

          order.timeline.forEach((item) => {
            tableRows.push(
              new TableRow({
                children: [
                  new TableCell({ children: [new Paragraph(item.time)] }),
                  new TableCell({ children: [new Paragraph(item.description)] }),
                ],
              })
            );
          });

          children.push(
            new Table({
              rows: tableRows,
              width: { size: 100, type: WidthType.PERCENTAGE },
            }),
            new Paragraph({ text: '' })
          );
        });
      } else {
        // 正常Timeline格式
        trackingData.timeline.forEach((item, index) => {
          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: `${index + 1}. `, bold: true }),
                new TextRun({ text: `[${item.time}]`, bold: true }),
              ],
            })
          );

          if (item.location) {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({ text: '地点: ' }),
                  new TextRun(item.location),
                  item.location_en ? new TextRun(` / ${item.location_en}`) : new TextRun(''),
                ],
              })
            );
          }

          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: '描述: ' }),
                new TextRun(item.description),
              ],
            })
          );

          if (item.description_en) {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({ text: '英文: ' }),
                  new TextRun({ text: item.description_en, italics: true }),
                ],
              })
            );
          }

          children.push(new Paragraph({ text: '' }));
        });
      }

      // 创建文档
      const doc = new Document({
        sections: [{
          children: children,
        }],
      });

      // 导出文档
      const blob = await docx.Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `物流查询_${trackingData.tracking_number}.docx`;
      link.click();
      URL.revokeObjectURL(url);
      message.success(`Word ${t.downloadSuccess}`);
    } catch (error) {
      console.error('Word导出错误:', error);
      message.error(`Word ${t.downloadError}`);
    }
  };

  // 下载菜单项
  const downloadMenuItems = [
    {
      key: 'txt',
      label: t.downloadTxt,
      onClick: downloadAsTxt,
    },
    {
      key: 'excel',
      label: t.downloadExcel,
      onClick: downloadAsExcel,
    },
    {
      key: 'word',
      label: t.downloadWord,
      onClick: downloadAsWord,
    },
  ];

  return (
    <div className="app-container">
      {/* 头部 */}
      <div className="header">
        <div className="logo-container">
          <div className="logo">
            {siteConfig.logoUrl ? (
              <img
                src={siteConfig.logoUrl}
                alt={siteConfig.companyName[language]}
                style={{ maxHeight: '60px', maxWidth: '200px', objectFit: 'contain' }}
              />
            ) : (
              <RocketOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
            )}
          </div>
          {siteConfig.companyName[language] && (
            <Title level={1} className="company-name">{siteConfig.companyName[language]}</Title>
          )}
          <div className="language-switch" onClick={toggleLanguage}>
            <span className={`lang-option ${language === 'zh' ? 'active' : ''}`}>中文</span>
            <span className="lang-divider">|</span>
            <span className={`lang-option ${language === 'en' ? 'active' : ''}`}>EN</span>
          </div>
        </div>
      </div>

      {/* 主内容 */}
      <div className="main-content">
        {/* 搜索卡片 - 仅客户专属页面显示 */}
        {customerSlug && (
        <Card className="search-card">
          <Search
            placeholder={t.searchPlaceholder}
            allowClear
            enterButton={<><SearchOutlined /> {t.searchButton}</>}
            size="large"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onSearch={handleSearch}
            loading={loading}
          />
        </Card>
        )}

        {/* 微信二维码 - 仅默认首页显示 */}
        {!customerSlug && !trackingData && !loading && (
          <div className="qrcode-card">
            <div className="qrcode-section">
              <div className="qrcode-wrapper">
                <img src="/qrcode.jpg" alt="WeChat QR Code" className="qrcode-img" />
              </div>
              <div className="qrcode-text">
                <span className="qrcode-title">
                  {language === 'zh' ? '定制专属查询页面' : 'Get Your Own Tracking Page'}
                </span>
                <span className="qrcode-desc">
                  {language === 'zh' ? '扫码添加微信，为您搭建专属物流查询系统' : 'Scan to contact us on WeChat for your custom tracking solution'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* 加载状态 */}
        {loading && (
          <div className="loading-container">
            <Spin size="large" tip={t.searching} />
          </div>
        )}

        {/* 查询结果 */}
        {!loading && trackingData && (
          <Card className="result-card">
            {/* 基本信息 - 横向布局 */}
            <div className="info-section">
              <Row gutter={[24, 16]}>
                {/* 第一行：快递单号 + 当前状态 */}
                <Col xs={12} sm={12} md={8}>
                  <div className="info-item">
                    <Text type="secondary" className="info-label">{t.trackingNumber}</Text>
                    <Text strong copyable className="info-value">{trackingData.tracking_number}</Text>
                  </div>
                </Col>
                <Col xs={12} sm={12} md={8}>
                  <div className="info-item">
                    <Text type="secondary" className="info-label">{t.currentStatus}</Text>
                    <div className="info-value">
                      <Tag
                        icon={getStatusConfig(trackingData.status).icon}
                        color={getStatusConfig(trackingData.status).color}
                      >
                        {trackingData.status}
                      </Tag>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* 第二行：发件地 + 收件地 */}
              <Row gutter={[24, 16]} style={{ marginTop: '16px' }}>
                <Col xs={12} sm={12} md={8}>
                  <div className="info-item">
                    <Text type="secondary" className="info-label">{t.sender}</Text>
                    <Text className="info-value">{trackingData.sender || '-'}</Text>
                  </div>
                </Col>
                <Col xs={12} sm={12} md={8}>
                  <div className="info-item">
                    <Text type="secondary" className="info-label">{t.receiver}</Text>
                    <Text className="info-value">{trackingData.receiver || '-'}</Text>
                  </div>
                </Col>
              </Row>

              {/* EXP物流单个订单：显示额外信息 */}
              {trackingData.courier === 'EXP物流' && trackingData.order_info && (
                <Row gutter={[24, 16]} style={{ marginTop: '16px' }}>
                  <Col xs={12} sm={12} md={8}>
                    <div className="info-item">
                      <Text type="secondary" className="info-label">{t.orderTime}</Text>
                      <Text className="info-value">{trackingData.order_info.order_time}</Text>
                    </div>
                  </Col>
                  <Col xs={12} sm={12} md={8}>
                    <div className="info-item">
                      <Text type="secondary" className="info-label">{t.totalPieces}</Text>
                      <Text className="info-value">{trackingData.order_info.total_pieces}</Text>
                    </div>
                  </Col>
                </Row>
              )}
            </div>

            {/* 分隔线 */}
            <div className="divider"></div>

            {/* 最新状态显示 */}
            {trackingData.latest_status && (
              <div className="latest-status-section">
                <Alert
                  message={t.latestStatus}
                  description={trackingData.latest_status}
                  type="info"
                  showIcon
                  icon={<ClockCircleOutlined />}
                />
              </div>
            )}

            {/* 物流轨迹 */}
            <div className="timeline-section">
              <div className="timeline-header-row">
                <Title level={4} className="section-title">{t.timeline}</Title>
                <Dropdown menu={{ items: downloadMenuItems }} placement="bottomRight">
                  <Button type="primary" icon={<DownloadOutlined />}>
                    {t.download}
                  </Button>
                </Dropdown>
              </div>

              {/* EXP物流特殊显示：多个捆绑订单 */}
              {trackingData.courier === 'EXP物流' && trackingData.orders && trackingData.orders.length > 0 ? (
                <div className="exp-orders-container">
                  {trackingData.orders.map((order, orderIndex) => (
                    <div key={orderIndex} className="exp-order-block">
                      {/* 订单头部信息 */}
                      <div className="exp-order-header">
                        <span className="exp-order-info">{t.orderNo}：{order.order_no}</span>
                        <span className="exp-order-info">{t.orderTime}：{order.order_time}</span>
                        <span className="exp-order-info">{t.totalPieces}：{order.total_pieces}</span>
                      </div>

                      {/* 订单物流轨迹表格 */}
                      <div className="exp-order-timeline">
                        <table className="exp-timeline-table">
                          <thead>
                            <tr>
                              <th>{t.statusLabel}</th>
                              <th>{t.timeLabel}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {order.timeline.map((item, itemIndex) => (
                              <tr key={itemIndex}>
                                <td>{item.description}</td>
                                <td>{item.time}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* 其他物流公司：正常Timeline显示 */
                <Timeline
                  items={trackingData.timeline.map((item, index) => ({
                    color: index === 0 ? 'green' : 'blue',
                    children: (
                      <div className="timeline-item">
                        <div className="timeline-header">
                          <span className="timeline-time">{item.time}</span>
                          {item.location && (
                            <span className="timeline-location">
                              <EnvironmentOutlined />
                              <span className="location-text">
                                {item.location}
                                {item.location_en && (
                                  <span className="location-en"> / {item.location_en}</span>
                                )}
                              </span>
                            </span>
                          )}
                        </div>
                        <div className="timeline-desc-wrapper">
                          <div className="timeline-desc">{item.description}</div>
                          {item.description_en && (
                            <div className="timeline-desc-en">{item.description_en}</div>
                          )}
                        </div>
                      </div>
                    ),
                  }))}
                />
              )}
            </div>
          </Card>
        )}
      </div>

      {/* 页脚 */}
      <div className="footer">
        <Text type="secondary">
          {customerInfo ? (customerInfo.display_name ? `© 2024 ${customerInfo.display_name}` : '') : t.footer}
        </Text>
        {customerInfo?.contact_phone && (
          <Text type="secondary" style={{ marginLeft: '16px' }}>
            联系电话: {customerInfo.contact_phone}
          </Text>
        )}
      </div>
    </div>
  );
}

export default App;
